import React, { useState } from "react";

import {
  Download,
  FileSpreadsheet,
  Info,
  Loader2,
  Upload,
} from "lucide-react";
import * as XLSX from "xlsx";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { useToast } from "~/hooks/use-toast";
import { useAddressImport } from "~/hooks/useAddressImport";
import { exportAddressesToExcel } from "~/services/addressExportService";

interface AddressImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface AddressImportData {
  address_id: string;
  street?: string;
  number?: string;
  city?: string;
  comuna?: string;
  region?: string;
}

export const AddressImportDialog: React.FC<AddressImportDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}): JSX.Element => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [importMethod, setImportMethod] = useState<"template" | "upload">(
    "template"
  );
  const { toast } = useToast();
  const { importAddresses } = useAddressImport();

  const handleDownloadTemplate = async () => {
    setIsExporting(true);
    try {
      await exportAddressesToExcel();
      toast({
        title: "Plantilla Descargada",
        description: "Se ha descargado el archivo con todas las direcciones.",
      });
    } catch (error) {
      console.error("Error downloading template:", error);
      toast({
        title: "Error",
        description: "No se pudo descargar la plantilla.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const parseExcelToAddresses = (file: File): Promise<AddressImportData[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "binary" });

          // Look for the "Direcciones" sheet first, then fallback to first non-instructions sheet
          const sheetName =
            workbook.SheetNames.find((name) => name === "Direcciones") ||
            workbook.SheetNames.find((name) => name !== "Instrucciones") ||
            workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            throw new Error(
              "El archivo debe contener encabezados y al menos una fila de datos"
            );
          }

          const headers = jsonData[0] as string[];
          const rows = jsonData.slice(1) as unknown[][];

          // Field mapping for address data
          const fieldMap: Record<string, keyof AddressImportData> = {
            "ID Dirección": "address_id",
            "ID Direccion": "address_id",
            address_id: "address_id",
            Calle: "street",
            street: "street",
            Número: "number",
            Numero: "number",
            number: "number",
            Ciudad: "city",
            city: "city",
            Comuna: "comuna",
            comuna: "comuna",
            Región: "region",
            Region: "region",
            region: "region",
          };

          const addresses: AddressImportData[] = rows
            .filter((row) => row && row.length > 0)
            .map((row) => {
              const addr: Partial<AddressImportData> = {};
              headers.forEach((header, index) => {
                const field = fieldMap[header];
                if (
                  field &&
                  row[index] !== undefined &&
                  row[index] !== null &&
                  row[index] !== ""
                ) {
                  addr[field] = String(row[index]).trim();
                }
              });
              return addr as AddressImportData;
            })
            .filter((addr) => addr.address_id);

          resolve(addresses);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error("Error leyendo archivo"));
      reader.readAsBinaryString(file);
    });
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    try {
      const addresses = await parseExcelToAddresses(file);

      if (addresses.length === 0) {
        toast({
          title: "Error",
          description:
            "No se encontraron direcciones válidas en el archivo. Asegúrese de que la columna 'ID Dirección' tenga valores.",
          variant: "destructive",
        });
        return;
      }

      await importAddresses(addresses);
      onSuccess?.();

      // Reset file input
      event.target.value = "";
    } catch (error) {
      console.error("Import error:", error);
      toast({
        title: "Error de importación",
        description:
          error instanceof Error ? error.message : "Error al importar.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Gestión de Direcciones</DialogTitle>
          <DialogDescription>
            Descargue la plantilla con todas las direcciones, edite y suba el
            archivo para aplicar cambios masivos.
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 space-y-4">
          {/* Method Selection */}
          <div className="flex gap-2">
            <Button
              variant={importMethod === "template" ? "default" : "outline"}
              onClick={() => setImportMethod("template")}
              disabled={isProcessing}
              className="flex-1"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Descargar Plantilla
            </Button>
            <Button
              variant={importMethod === "upload" ? "default" : "outline"}
              onClick={() => setImportMethod("upload")}
              disabled={isProcessing}
              className="flex-1"
            >
              <Upload className="mr-2 h-4 w-4" />
              Subir Excel
            </Button>
          </div>

          {importMethod === "template" && (
            <div className="space-y-4">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>Flujo de trabajo:</strong>
                  <ol className="mt-2 list-inside list-decimal space-y-1 text-sm">
                    <li>Descargue la plantilla con todas las direcciones</li>
                    <li>
                      Edite solo las columnas permitidas: Calle, Número, Ciudad,
                      Comuna, Región
                    </li>
                    <li>Guarde el archivo como Excel (.xlsx)</li>
                    <li>Suba el archivo usando "Subir Excel"</li>
                  </ol>
                </AlertDescription>
              </Alert>

              <div className="flex justify-center">
                <Button
                  onClick={handleDownloadTemplate}
                  disabled={isExporting}
                  className="w-full max-w-sm"
                >
                  {isExporting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  {isExporting
                    ? "Exportando..."
                    : "Descargar Todas las Direcciones"}
                </Button>
              </div>
            </div>
          )}

          {importMethod === "upload" && (
            <div className="space-y-4">
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                disabled={isProcessing}
                className="file:mr-4 file:rounded-full file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary-foreground"
              />

              {isProcessing && (
                <div className="flex items-center justify-center py-4">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  <span className="ml-2 text-sm text-muted-foreground">
                    Procesando...
                  </span>
                </div>
              )}

              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>Solo actualizaciones:</strong>
                  <ul className="mt-2 list-inside list-disc space-y-1 text-sm">
                    <li>
                      Las direcciones se identifican por la columna "ID
                      Dirección"
                    </li>
                    <li>Solo se actualizan direcciones existentes</li>
                    <li>No se crean ni eliminan direcciones</li>
                    <li>
                      Las columnas "Nombre Cliente" y "RUT Cliente" son solo de
                      referencia
                    </li>
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isProcessing}
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
